import { ChevronDown } from '@/components/ui/icons';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import {
  ImplicitPrimitive,
  ImplicitPrimitiveParams,
  ROVER_WHEEL_DEFAULTS,
} from '@/config/implicitPrimitives';

import {
  pickerContentClass,
  pickerHeroItemBaseClass,
  pickerHeroWrapperClass,
  pickerItemActiveClass,
  pickerItemInactiveClass,
  pillWidthClass,
} from '../dropdownStyles';

import { WHEELS, SubTab } from './data';

interface WheelsDropdownProps {
  tab: SubTab;
  vehicleSubTab: string;
  implicitPrimitive: ImplicitPrimitive;
  setVehicleSubTab: (subTab: string) => void;
  setImplicitPrimitive: (p: ImplicitPrimitive) => void;
  setImplicitPrimitiveParams: (p: ImplicitPrimitiveParams) => void;
}

export function WheelsDropdown({
  tab,
  vehicleSubTab,
  implicitPrimitive,
  setVehicleSubTab,
  setImplicitPrimitive,
  setImplicitPrimitiveParams,
}: WheelsDropdownProps) {
  const isActive = vehicleSubTab === tab.value;
  const activeWheel = WHEELS.find((w) => w.value === implicitPrimitive);
  // Always show a hero: the active selection's, else the first in
  // the list (so the dropdown reads as a hero'd chooser on sight).
  const triggerHero =
    (isActive && activeWheel ? activeWheel.hero : undefined) ?? WHEELS[0]?.hero;
  const handlePick = (w: (typeof WHEELS)[number]) => {
    setVehicleSubTab('wheels');
    setImplicitPrimitive(w.value);
    if (w.value === 'rover-wheel') {
      setImplicitPrimitiveParams({
        kind: 'rover-wheel',
        params: ROVER_WHEEL_DEFAULTS,
      });
    }
  };
  return (
    <DropdownMenu key={tab.value}>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            pillWidthClass,
            'flex items-center justify-center gap-2 self-stretch overflow-hidden rounded-md border px-4 text-sm transition-colors',
            isActive
              ? 'border-[#107d40] bg-[#070909] text-[#107d40] hover:border-[#107d40] hover:bg-[#0f3d24]'
              : 'border-[#737373] text-telekinesis-neutral-50 hover:border-[#107d40] hover:bg-[#0f3d24]',
          )}
        >
          <span className="flex items-center gap-1 leading-tight">
            {tab.label}
            <ChevronDown className="h-3 w-3" />
          </span>
          {triggerHero && (
            <div className="flex w-14 flex-shrink-0 items-center justify-center self-stretch">
              <img
                src={triggerHero}
                alt=""
                aria-hidden="true"
                className="h-full object-contain"
              />
            </div>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className={pickerContentClass}
        style={{
          minWidth: 'var(--radix-dropdown-menu-trigger-width)',
        }}
      >
        {WHEELS.map((w) => (
          <DropdownMenuItem
            key={w.value}
            onClick={() => handlePick(w)}
            className={cn(
              pickerHeroItemBaseClass,
              implicitPrimitive === w.value && isActive
                ? pickerItemActiveClass
                : pickerItemInactiveClass,
            )}
          >
            <span className="font-medium">{w.label}</span>
            <div className={pickerHeroWrapperClass}>
              <img
                src={w.hero}
                alt=""
                aria-hidden="true"
                className="h-full object-contain"
              />
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
